import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

interface CardModel {
    fields: {
        cardTitle: string;
        image: string;
        imageAltText: string;
        description: string;
        link: LinkFieldValue;
        linksHtml: string;
    };
}

const enum fields {
    title = "title",
    subtitle = "subtitle",
    cardStyle = "cardStyle",
    cardsPerRow = "cardsPerRow",
    cards = "cards",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.subtitle]: string;
        [fields.cardStyle]: "bordered" | "plain";
        [fields.cardsPerRow]: number;
        [fields.cards]: CardModel[];
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.customCSS]: string;
    };
}

export interface CategoryCardsStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    subtitleText?: TypographyPresentationProps;
    cardsContainer?: GridContainerProps;
    cardGridItem?: GridItemProps;
    cardWrapper?: InjectableCss;
    borderedCardWrapper?: InjectableCss;
    cardImageLink?: LinkPresentationProps;
    cardImage?: LazyImageProps;
    cardTitleLink?: LinkPresentationProps;
    cardTitleText?: TypographyPresentationProps;
    cardDescription?: InjectableCss;
    cardLinks?: InjectableCss;
    buttonWrapper?: InjectableCss;
    button?: ClickablePresentationProps;
}

export const categoryCardsStyles: CategoryCardsStyles = {
    wrapper: {
        css: css`
            padding: 40px 0;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            text-transform: uppercase;
            font-weight: 700;
            font-size: 24px;
            margin-bottom: 8px;
        `,
    },
    subtitleText: {
        css: css`
            text-align: center;
            font-size: 15px;
            max-width: 640px;
            margin: 0 auto 36px auto;
        `,
    },
    cardsContainer: {
        gap: 24,
        css: css`
            max-width: 1140px;
            margin: 0 auto;
        `,
    },
    cardWrapper: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    borderedCardWrapper: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            border: 1px solid #bebfbf;
            padding: 20px;
            transition: background-color 0.15s ease-in-out;
            &:hover {
                background-color: #e8e9e9;
            }
        `,
    },
    cardImageLink: {
        css: css`
            width: 100%;
            display: block;
            margin-bottom: 16px;
        `,
    },
    cardImage: {
        width: "100%",
        css: css`
            img {
                width: 100%;
                height: auto;
                object-fit: cover;
            }
        `,
    },
    cardTitleLink: {
        color: "text.main",
        css: css`
            margin-bottom: 10px;
        `,
        typographyProps: {
            variant: "h3",
            css: css`
                font-weight: 700;
                font-size: 16px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 0;
            `,
        },
    },
    cardTitleText: {
        variant: "h3",
        css: css`
            font-weight: 700;
            font-size: 16px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 10px;
        `,
    },
    cardDescription: {
        css: css`
            font-size: 13px;
            line-height: 1.5;
        `,
    },
    cardLinks: {
        css: css`
            font-size: 14px;
            a {
                display: block;
                color: #000;
                text-decoration: underline;
                font-weight: 600;
                margin-bottom: 10px;
            }
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 36px;
        `,
    },
    button: {
        css: css`
            background: #ffd100;
            color: #000;
            font-weight: 800;
            font-size: 15px;
            line-height: 15px;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            text-decoration: none;
            justify-content: center;
            padding: 24px 32px;
            border-radius: 0;
            &:hover {
                background: #e6bc00;
            }
        `,
    },
};

const styles = categoryCardsStyles;

const widthsByCardsPerRow: { [key: number]: GridItemProps["width"] } = {
    2: [12, 6, 6, 6, 6],
    3: [12, 6, 4, 4, 4],
    4: [12, 6, 4, 3, 3],
};

const CardLink = ({
    linkValue,
    children,
    ...linkProps
}: { linkValue: LinkFieldValue; children: React.ReactNode } & LinkPresentationProps) => {
    const link = useGetLink(linkValue);
    if (!link?.url) {
        return null;
    }

    return (
        <Link href={link.url} {...linkProps}>
            {children}
        </Link>
    );
};

/**
 * Custom card grid per the SL31 Industries designs. Covers the bordered text cards
 * ("Shop Foodservice Categories"), image cards with descriptions (Industries landing grid),
 * and image cards with link lists ("Shop by Restaurant Application").
 */
const CategoryCards = ({ fields }: OwnProps) => {
    const buttonLink = useGetLink(fields.buttonLink);
    const cards = fields.cards || [];

    if (cards.length === 0) {
        return null;
    }

    const cardGridItemProps: GridItemProps = {
        width: widthsByCardsPerRow[fields.cardsPerRow] || widthsByCardsPerRow[4],
        ...styles.cardGridItem,
    };

    const cardWrapperStyles = fields.cardStyle === "bordered" ? styles.borderedCardWrapper : styles.cardWrapper;

    const customCssWrapper = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper} data-test-selector="categoryCards">
            {fields.title && <Typography {...styles.titleText}>{fields.title}</Typography>}
            {fields.subtitle && (
                <Typography as="p" {...styles.subtitleText}>
                    {fields.subtitle}
                </Typography>
            )}
            <GridContainer {...styles.cardsContainer}>
                {cards.map((card, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <GridItem key={index} {...cardGridItemProps}>
                        <StyledWrapper {...cardWrapperStyles}>
                            {card.fields.image && (
                                <CardLink linkValue={card.fields.link} {...styles.cardImageLink}>
                                    <LazyImage
                                        src={parseImageUrl(card.fields.image).url}
                                        altText={card.fields.imageAltText || card.fields.cardTitle}
                                        {...styles.cardImage}
                                    />
                                </CardLink>
                            )}
                            {card.fields.cardTitle && (
                                <CardTitle title={card.fields.cardTitle} linkValue={card.fields.link} />
                            )}
                            {card.fields.description && (
                                <StyledWrapper {...styles.cardDescription}>
                                    {parse(card.fields.description, parserOptions)}
                                </StyledWrapper>
                            )}
                            {card.fields.linksHtml && (
                                <StyledWrapper {...styles.cardLinks}>
                                    {parse(card.fields.linksHtml, parserOptions)}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </GridItem>
                ))}
            </GridContainer>
            {fields.buttonLabel && buttonLink?.url && (
                <StyledWrapper {...styles.buttonWrapper}>
                    <Clickable href={buttonLink.url} {...styles.button} data-test-selector="categoryCardsButton">
                        {fields.buttonLabel}
                    </Clickable>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const CardTitle = ({ title, linkValue }: { title: string; linkValue: LinkFieldValue }) => {
    const link = useGetLink(linkValue);
    if (link?.url) {
        return (
            <Link href={link.url} {...styles.cardTitleLink}>
                {title}
            </Link>
        );
    }

    return <Typography {...styles.cardTitleText}>{title}</Typography>;
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: CategoryCards,
    definition: {
        group: "Categories",
        icon: "LayoutGrid",
        displayName: "Category Cards",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
                tab: contentTab,
            },
            {
                name: fields.subtitle,
                displayName: "Subtitle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 1,
                tab: contentTab,
            },
            {
                name: fields.cards,
                displayName: "Cards",
                editorTemplate: "ListField",
                getDisplay: ({ fields: { cardTitle } }: HasFields) => cardTitle || "Card",
                defaultValue: [],
                fieldType: "Translatable",
                sortOrder: 2,
                tab: contentTab,
                fieldDefinitions: [
                    {
                        name: "cardTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "imageAltText",
                        displayName: "Image Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: (widget: HasFields) => !!widget.fields.image,
                    },
                    {
                        name: "link",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "description",
                        displayName: "Description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        extendedConfig: { height: 120 },
                    },
                    {
                        name: "linksHtml",
                        displayName: "Links",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        tooltip: "Optional list of links displayed under the title, one per line.",
                    },
                ],
            },
            {
                name: fields.cardStyle,
                displayName: "Card Style",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Plain", value: "plain" },
                    { displayName: "Bordered with hover", value: "bordered" },
                ],
                hideEmptyOption: true,
                defaultValue: "plain",
                fieldType: "General",
                sortOrder: 3,
                tab: settingsTab,
            },
            {
                name: fields.cardsPerRow,
                displayName: "Cards Per Row",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "2", value: 2 },
                    { displayName: "3", value: 3 },
                    { displayName: "4", value: 4 },
                ],
                hideEmptyOption: true,
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 4,
                tab: settingsTab,
            },
            {
                name: fields.buttonLabel,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 5,
                tab: settingsTab,
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                sortOrder: 6,
                tab: settingsTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: "",
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            },
        ],
    },
};

export default widgetModule;
